import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Mail } from "lucide-react";

const ROLES = [
  { key: "admin",        label: "ADMIN",        tone: "bg-orange-500/20 text-orange-300 border border-orange-500/40" },
  { key: "registration", label: "REGISTRATION", tone: "bg-rose-500/20 text-rose-300 border border-rose-500/40" },
  { key: "bar",          label: "BAR",          tone: "bg-teal-500/20 text-teal-300 border border-teal-500/40" },
];
const LEGACY_ROLE = { reception: "registration", tickets: "bar", bartender: "bar", staff: "bar" };
const normalizeRole = (r) => LEGACY_ROLE[r] || r;
const roleTone = (r) => ROLES.find((x) => x.key === normalizeRole(r))?.tone || "bg-slate-800 text-slate-300";
const roleLabel = (r) => ROLES.find((x) => x.key === normalizeRole(r))?.label || (r || "").toUpperCase();

const StatusPill = ({ s }) => {
  const map = {
    ACTIVE:   "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    INVITED:  "bg-amber-500/20 text-amber-300 border-amber-500/30",
    DISABLED: "bg-red-500/20 text-red-300 border-red-500/30",
  };
  const status = s || "ACTIVE";
  return <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded border ${map[status] || map.ACTIVE}`}>{status}</span>;
};

export default function RolesPage() {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  const [pending, setPending] = useState(null);
  const [inv, setInv] = useState({ email: "", name: "", role: "registration" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data.items));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!target || !pending) return;
    try {
      await api.patch(`/users/${target.id}/role`, { role: pending });
      toast.success(`Role updated to ${roleLabel(pending)}`);
      setTarget(null); setPending(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const invite = async () => {
    if (!inv.email) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/users", { email: inv.email.trim().toLowerCase(), name: inv.name || null, role: inv.role });
      toast.success(data.status === "invited" ? "Invited — user can now sign in with Google" : "User updated");
      setInv({ email: "", name: "", role: "registration" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">Access Control</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">ROLES & ACCESS</h1>
        <p className="text-slate-400 text-sm mt-2">Only invited emails can sign in with Google. Admin seeds the users; first Google login activates the account.</p>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl p-5 space-y-4" data-testid="invite-card">
        <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-orange-400 font-bold">
          <UserPlus size={14} /> Invite a user
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Email (must match their Google account)</Label>
            <Input value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} placeholder="person@example.com" className="bg-[#0B1120] border-slate-700 text-white" data-testid="invite-email" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Display Name (optional)</Label>
            <Input value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} placeholder="e.g. María" className="bg-[#0B1120] border-slate-700 text-white" data-testid="invite-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Role</Label>
            <Select value={inv.role} onValueChange={(v) => setInv({ ...inv, role: v })}>
              <SelectTrigger className="bg-[#0B1120] border-slate-700 text-white" data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="registration">Registration</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={invite} disabled={saving} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="invite-submit">
          <Mail size={16} className="mr-1" /> {saving ? "Saving…" : "Send Invite"}
        </Button>
      </div>

      <div className="bg-[#131D33] border border-slate-800 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-widest text-slate-400 border-b border-slate-800 bg-black/20">
          <div className="col-span-3">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-slate-800">
          {users.length === 0 && <div className="p-8 text-center text-slate-500">No users.</div>}
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-2 md:grid-cols-12 px-4 py-3 items-center gap-2" data-testid={`user-row-${u.email}`}>
              <div className="md:col-span-3 text-white font-semibold truncate">{u.name || "—"}</div>
              <div className="md:col-span-4 text-slate-300 text-sm truncate">{u.email}</div>
              <div className="md:col-span-2">
                <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${roleTone(u.role)}`}>{roleLabel(u.role)}</span>
              </div>
              <div className="md:col-span-1"><StatusPill s={u.status} /></div>
              <div className="md:col-span-2 flex gap-1 justify-end">
                <Button size="sm" onClick={() => { setTarget(u); setPending(u.role); }} className="bg-slate-800 border border-slate-700 hover:bg-slate-700 text-slate-200 h-8" data-testid={`change-role-btn-${u.email}`}>Role</Button>
                <Button size="sm" variant="outline" onClick={() => remove(u)} className="bg-slate-900 border-red-900/50 text-red-300 h-8" data-testid={`remove-user-${u.email}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(v) => !v && (setTarget(null), setPending(null))}>
        <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">CHANGE ROLE</DialogTitle></DialogHeader>
          {target && (
            <>
              <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">User</span><b>{target.name}</b></div>
                <div className="flex justify-between"><span className="text-slate-400">Email</span><span>{target.email}</span></div>
                <div className="flex justify-between mt-1"><span className="text-slate-400">Current</span><span className={`px-2 py-0.5 rounded text-xs font-bold ${roleTone(target.role)}`}>{roleLabel(target.role)}</span></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {ROLES.map((r) => (
                  <button key={r.key} onClick={() => setPending(r.key)} className={`rounded-xl p-3 font-display font-black tracking-tight text-sm ${pending === r.key ? `${r.tone} scale-[1.02]` : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{r.label}</button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={() => { setTarget(null); setPending(null); }} className="flex-1 bg-slate-800 border-slate-700 text-slate-200">CANCEL</Button>
                <Button onClick={save} disabled={!pending || pending === target.role} className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold">CONFIRM</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
